import React, { useEffect, useRef, useState } from "react";
import { Maximize, Minimize, ShieldAlert, Cpu, Activity, Globe } from "lucide-react";
import WorldMapBackdrop from "./WorldMapBackdrop";

// Coordinates for simulation
const LOCATIONS = [
  { lat: 40.7128, lng: -74.0060, name: "New York", country: "United States", code: "US" },
  { lat: 51.5074, lng: -0.1278, name: "London", country: "United Kingdom", code: "GB" },
  { lat: 35.6762, lng: 139.6503, name: "Tokyo", country: "Japan", code: "JP" },
  { lat: 1.3521, lng: 103.8198, name: "Singapore", country: "Singapore", code: "SG" },
  { lat: 28.6139, lng: 77.2090, name: "New Delhi", country: "India", code: "IN" },
  { lat: 37.7749, lng: -122.4194, name: "San Francisco", country: "United States", code: "US" },
  { lat: 48.8566, lng: 2.3522, name: "Paris", country: "France", code: "FR" },
  { lat: 55.7558, lng: 37.6173, name: "Moscow", country: "Russia", code: "RU" },
  { lat: -33.8688, lng: 151.2093, name: "Sydney", country: "Australia", code: "AU" },
  { lat: -23.5505, lng: -46.6333, name: "Sao Paulo", country: "Brazil", code: "BR" },
  { lat: 19.0760, lng: 72.8777, name: "Mumbai", country: "India", code: "IN" },
  { lat: 39.9042, lng: 116.4074, name: "Beijing", country: "China", code: "CN" },
  { lat: 25.2048, lng: 55.2708, name: "Dubai", country: "United Arab Emirates", code: "AE" },
  { lat: -1.2921, lng: 36.8219, name: "Nairobi", country: "Kenya", code: "KE" },
  { lat: 52.5200, lng: 13.4050, name: "Berlin", country: "Germany", code: "DE" },
];

const ATTACK_TYPES = [
  { name: "Malware Hijack", class: "Malware" },
  { name: "Credential Stuffing", class: "Brute Force" },
  { name: "Phishing Redirect", class: "Social Eng" },
  { name: "GPS Spoof Dect", class: "GPS Hijack" },
  { name: "Fingerprint Spoof", class: "Identity" },
  { name: "OSINT Scraping", class: "Recon" },
];

const PROTOCOLS = ["HTTPS", "HTTP/3", "TLS v1.3", "UDP/1194", "TCP/443", "DNS/53"];

function getRandomLocation(excludeName = "") {
  let loc = LOCATIONS[Math.floor(Math.random() * LOCATIONS.length)];
  while (loc.name === excludeName) {
    loc = LOCATIONS[Math.floor(Math.random() * LOCATIONS.length)];
  }
  return loc;
}

// High-fidelity pre-mapped SVG coordinates for simulation cities (calibrated inland to guarantee they land squarely on land, far from the sea)
const CITY_COORDINATES = {
  "New York": { x: 185, y: 410 },
  "London": { x: 399, y: 378 },
  "Tokyo": { x: 710, y: 405 },
  "Singapore": { x: 650, y: 525 },
  "New Delhi": { x: 595, y: 460 },
  "San Francisco": { x: 145, y: 415 },
  "Paris": { x: 408, y: 402 },
  "Moscow": { x: 472, y: 350 },
  "Sydney": { x: 720, y: 635 },
  "Sao Paulo": { x: 310, y: 580 },
  "Mumbai": { x: 595, y: 480 },
  "Beijing": { x: 640, y: 415 },
  "Dubai": { x: 520, y: 475 },
  "Nairobi": { x: 502, y: 535 },
  "Berlin": { x: 428, y: 385 }
};

// Convert Geographical coordinates to flat SVG viewBox space coordinates
const getSVGCoordinates = (lat, lng, cityName = "") => {
  // Use high-fidelity pre-mapped coordinates if available
  if (cityName && CITY_COORDINATES[cityName]) {
    return CITY_COORDINATES[cityName];
  }
  
  // High-fidelity linear regression mapping fallback for dynamic locations
  const x = 2.396 * lng + 398.3;
  const y = 540.8 - 3.22 * lat;
  return { x, y };
};

const ThreatMap = () => {
  const containerRef = useRef();
  const [isMaximized, setIsMaximized] = useState(false);
  const [arcs, setArcs] = useState([]);
  const [points, setPoints] = useState([]);
  const [logs, setLogs] = useState([]);
  const [attacksCount, setAttacksCount] = useState(62894107);
  const [activeAttacker, setActiveAttacker] = useState("");
  const [activeTarget, setActiveTarget] = useState("");

  // Statistics State
  const [topCountries, setTopCountries] = useState([
    { name: "United States", code: "US", count: 24890, percent: 35 },
    { name: "India", code: "IN", count: 18451, percent: 26 },
    { name: "Germany", code: "DE", count: 9225, percent: 13 },
    { name: "Japan", code: "JP", count: 8530, percent: 12 },
    { name: "United Kingdom", code: "GB", count: 5621, percent: 8 },
  ]);

  const [topAttacks] = useState([
    { name: "Fingerprint Spoof", count: 42, percent: 42 },
    { name: "GPS Spoof", count: 28, percent: 28 },
    { name: "Malware", count: 15, percent: 15 },
    { name: "Brute Force", count: 10, percent: 10 },
    { name: "Phishing", count: 5, percent: 5 },
  ]);

  const [topIndustries] = useState([
    { name: "Education/Research", percent: 38 },
    { name: "Finance/Banking", percent: 26 },
    { name: "Healthcare", percent: 18 },
    { name: "Technology/Software", percent: 12 },
    { name: "Utilities/Energy", percent: 6 },
  ]);

  // Fullscreen natively
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsMaximized(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      if (containerRef.current?.requestFullscreen) {
        containerRef.current.requestFullscreen().catch((err) => {
          console.error(`Error entering fullscreen: ${err.message}`);
        });
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  // Attacks Scoreboard Tick-up simulation
  useEffect(() => {
    const scoreboardInterval = setInterval(() => {
      const increment = Math.floor(Math.random() * 80) + 15;
      setAttacksCount((prev) => prev + increment);
    }, 700);

    return () => clearInterval(scoreboardInterval);
  }, []);

  // Cyber Attacks Simulation
  useEffect(() => {
    const attackInterval = setInterval(() => {
      const source = getRandomLocation();
      const target = getRandomLocation(source.name);

      const srcXY = getSVGCoordinates(source.lat, source.lng, source.name);
      const tgtXY = getSVGCoordinates(target.lat, target.lng, target.name);

      // Compute curved bezier path (quadratic curve)
      const dx = tgtXY.x - srcXY.x;
      const dy = tgtXY.y - srcXY.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const mx = (srcXY.x + tgtXY.x) / 2;
      const my = (srcXY.y + tgtXY.y) / 2 - dist * 0.18; // curve height

      const id = Math.random().toString(36).substring(2, 9);
      const attackType = ATTACK_TYPES[Math.floor(Math.random() * ATTACK_TYPES.length)];
      const protocol = PROTOCOLS[Math.floor(Math.random() * PROTOCOLS.length)];

      const newArc = {
        id,
        path: `M ${srcXY.x} ${srcXY.y} Q ${mx} ${my} ${tgtXY.x} ${tgtXY.y}`,
        source,
        target,
        color: Math.random() > 0.5 ? "#f43f5e" : "#06b6d4",
      };

      const newPointSrc = {
        id: `${id}-src`,
        x: srcXY.x,
        y: srcXY.y,
        color: "#f43f5e",
        label: source.name,
      };

      const newPointTgt = {
        id: `${id}-tgt`,
        x: tgtXY.x,
        y: tgtXY.y,
        color: "#06b6d4",
        label: target.name,
      };

      const isBlocked = Math.random() > 0.3;

      const newLog = {
        id,
        time: new Date().toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        source: source.country,
        sourceCode: source.code,
        target: target.country,
        targetCode: target.code,
        type: attackType.name,
        class: attackType.class,
        protocol,
        status: isBlocked ? "BLOCKED" : "RECORDED",
      };

      // Set active highlight states
      setActiveAttacker(source.code);
      setActiveTarget(target.code);

      // Add to animations
      setArcs((prev) => [...prev, newArc].slice(-8));
      setPoints((prev) => [...prev, newPointSrc, newPointTgt].slice(-16));
      setLogs((prev) => [newLog, ...prev].slice(0, 10));

      // Slightly tick up top lists
      setTopCountries((prev) => {
        return prev.map((c) => {
          if (c.code === target.code) {
            const nextCount = c.count + 1;
            return { ...c, count: nextCount };
          }
          return c;
        }).sort((a, b) => b.count - a.count);
      });

      // Clear highlights shortly
      setTimeout(() => {
        setArcs((prev) => prev.filter((a) => a.id !== id));
        setPoints((prev) => prev.filter((p) => p.id !== `${id}-src` && p.id !== `${id}-tgt`));
      }, 2000);

    }, 1300);

    return () => clearInterval(attackInterval);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`transition-all duration-500 bg-[#030712] text-white flex flex-col font-mono relative overflow-hidden select-none border border-cyan-500/20 shadow-2xl rounded-xl ${
        isMaximized ? "w-screen h-screen" : "w-full min-h-[680px] h-[720px]"
      }`}
    >
      {/* Dynamic inline styles for SVG neon laser animations */}
      <style>{`
        @keyframes laser-arc-dash {
          to {
            stroke-dashoffset: 0;
          }
        }
        @keyframes pulse-node {
          0% {
            transform: scale(0.5);
            opacity: 1;
          }
          100% {
            transform: scale(3.5);
            opacity: 0;
          }
        }
        .laser-arc {
          stroke-dasharray: 20, 80;
          stroke-dashoffset: 200;
          animation: laser-arc-dash 2s linear infinite;
        }
        .pulse-ring {
          transform-origin: center;
          animation: pulse-node 1.5s ease-out infinite;
        }
        .scoreboard-neon {
          text-shadow: 0 0 8px rgba(34, 197, 94, 0.6), 0 0 20px rgba(34, 197, 94, 0.3);
        }
        .panel-glow {
          box-shadow: 0 0 15px rgba(6, 182, 212, 0.05) inset;
        }
      `}</style>

      {/* Cyber Grid Background layer */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(18,24,38,0.4)_1px,transparent_1px),linear-gradient(90deg,rgba(18,24,38,0.4)_1px,transparent_1px)] bg-[size:30px_30px] pointer-events-none" />

      {/* ── HIGH-TECH TOP HEADER ── */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-[#090d16] border-b border-cyan-500/20 px-6 py-4 relative z-10 shrink-0">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-3 h-3 rounded-full bg-cyan-500 animate-ping absolute" />
            <div className="w-3 h-3 rounded-full bg-cyan-500" />
          </div>
          <div>
            <h2 className="text-sm font-bold tracking-widest text-cyan-400 uppercase">Live Global Threat Map</h2>
            <div className="flex items-center gap-3 text-[10px] text-gray-500 mt-0.5">
              <span className="flex items-center gap-1"><Activity className="w-3 h-3" /> FEED: REALTIME</span>
              <span className="flex items-center gap-1"><Cpu className="w-3 h-3" /> SENSORS: ONLINE</span>
            </div>
          </div>
        </div>

        {/* LED Scoreboard Counter */}
        <div className="flex flex-col items-center bg-[#020617] border border-cyan-500/30 rounded px-6 py-1.5 shadow-[0_0_15px_rgba(6,182,212,0.1)]">
          <span className="text-[9px] uppercase tracking-[0.25em] text-cyan-500/60 font-semibold mb-0.5">ATTACKS DETECTED TODAY</span>
          <span className="font-mono text-xl md:text-2xl font-bold tracking-widest text-green-400 scoreboard-neon">
            {attacksCount.toLocaleString()}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right hidden lg:block">
            <span className="text-[10px] text-rose-500 font-semibold uppercase tracking-widest block">Threat Level: High</span>
            <span className="text-[9px] text-gray-500 block mt-0.5">MONITORING 150+ CAPTURE CHANNELS</span>
          </div>

          <button
            onClick={toggleFullscreen}
            className="p-2 border border-cyan-500/30 bg-[#0c1220] hover:bg-cyan-500/20 hover:border-cyan-400 text-cyan-400 rounded-lg transition-all"
            title={isMaximized ? "Exit Fullscreen" : "Fullscreen Cyber Display"}
          >
            {isMaximized ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* ── CORE DASHBOARD LAYOUT ── */}
      <div className="flex flex-1 w-full h-full min-h-0 relative z-10">
        
        {/* LEFT SIDEBAR: LIVE LOG FEED (Ticker style) */}
        <div className="w-[310px] bg-[#050912]/95 border-r border-cyan-500/20 p-4 flex flex-col hidden lg:flex min-h-0 shrink-0">
          <div className="flex items-center gap-2 mb-3 border-b border-cyan-500/20 pb-2">
            <ShieldAlert className="w-4 h-4 text-rose-500 animate-pulse" />
            <span className="text-[10px] tracking-widest font-bold text-rose-400 uppercase">Live Attack Log Stream</span>
          </div>
          <div className="flex-1 overflow-y-auto space-y-2.5 pr-1 scrollbar-thin scrollbar-thumb-cyan-500/20 scrollbar-track-transparent">
            {logs.map((log) => (
              <div
                key={log.id}
                className="bg-[#0b101b] border border-cyan-500/10 hover:border-cyan-500/20 rounded p-2.5 transition-all text-[11px]"
              >
                <div className="flex justify-between items-center mb-1 text-[10px]">
                  <span className="text-gray-500">[{log.time}]</span>
                  <span
                    className={`px-1 rounded font-bold text-[9px] tracking-wider ${
                      log.status === "BLOCKED"
                        ? "bg-rose-500/10 text-rose-400 border border-rose-500/20"
                        : "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20"
                    }`}
                  >
                    {log.status}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 mb-1 text-gray-300">
                  <span className="px-1 py-0.2 bg-red-950 text-red-400 rounded text-[9px] font-bold uppercase">{log.sourceCode}</span>
                  <span className="text-cyan-500/50">⟶</span>
                  <span className="px-1 py-0.2 bg-cyan-950 text-cyan-400 rounded text-[9px] font-bold uppercase">{log.targetCode}</span>
                </div>
                <div className="flex justify-between items-center text-gray-400 text-[10px] mt-1 border-t border-gray-900 pt-1.5">
                  <span className="text-green-400 truncate max-w-[120px]">{log.type}</span>
                  <span className="text-gray-500/70">{log.protocol}</span>
                </div>
              </div>
            ))}
            {logs.length === 0 && (
              <div className="text-gray-500 italic text-center py-10 text-xs">Awaiting live cyber telemetry...</div>
            )}
          </div>
        </div>

        {/* CENTER COLUMN: FLAT MAP DISPLAY */}
        <div className="flex-1 h-full flex items-center justify-center p-4 relative bg-[#010409]">
          <div className="w-full h-full max-h-[500px] aspect-[784/458] relative">
            
            {/* World Map Backdrop component */}
            <WorldMapBackdrop activeAttackerCode={activeAttacker} activeTargetCode={activeTarget} />

            {/* Dynamic Attack Canvas Layer */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="30.767 241.591 784.077 458.627"
            >
              {/* Attack Laser Arcs */}
              {arcs.map((arc) => (
                <path
                  key={arc.id}
                  d={arc.path}
                  fill="none"
                  stroke={arc.color}
                  strokeWidth="1.8"
                  opacity="0.8"
                  className="laser-arc"
                  style={{
                    filter: `drop-shadow(0 0 6px ${arc.color})`,
                  }}
                />
              ))}

              {/* Pulsing rings at impact and start coordinates */}
              {points.map((p) => (
                <g key={p.id}>
                  {/* Outer pulsing ring */}
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r="4"
                    fill="none"
                    stroke={p.color}
                    strokeWidth="1"
                    className="pulse-ring"
                    style={{
                      transformOrigin: `${p.x}px ${p.y}px`,
                    }}
                  />
                  {/* Central glowing solid node */}
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r="2.5"
                    fill={p.color}
                    style={{
                      filter: `drop-shadow(0 0 4px ${p.color})`,
                    }}
                  />
                </g>
              ))}
            </svg>

            {/* Attacker -> Target Label Overlay */}
            {arcs.length > 0 && (
              <div className="absolute bottom-4 left-4 bg-black/85 border border-cyan-500/30 rounded px-3 py-1.5 backdrop-blur-sm pointer-events-none transition-all duration-300 font-mono text-[10px] space-y-1">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-ping" />
                  <span className="text-rose-400 font-bold uppercase">Attacker:</span>
                  <span className="text-gray-300 font-bold">{arcs[arcs.length - 1].source.name} ({arcs[arcs.length - 1].source.country})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-ping" />
                  <span className="text-cyan-400 font-bold uppercase">Target:</span>
                  <span className="text-gray-300 font-bold">{arcs[arcs.length - 1].target.name} ({arcs[arcs.length - 1].target.country})</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDEBAR: CYBER STATISTICS PANEL */}
        <div className="w-[300px] bg-[#050912]/95 border-l border-cyan-500/20 p-4 flex flex-col hidden xl:flex min-h-0 shrink-0">
          <div className="flex items-center gap-2 mb-4 border-b border-cyan-500/20 pb-2">
            <Globe className="w-4 h-4 text-cyan-400" />
            <span className="text-[10px] tracking-widest font-bold text-cyan-400 uppercase">Threat Analytics</span>
          </div>

          <div className="flex-1 overflow-y-auto space-y-5 pr-1 scrollbar-thin scrollbar-thumb-cyan-500/20 scrollbar-track-transparent">
            {/* Top Target Countries */}
            <div>
              <span className="text-[9px] uppercase tracking-wider text-cyan-500/60 font-bold block mb-2">Top Target Countries</span>
              <div className="space-y-2">
                {topCountries.map((c) => (
                  <div key={c.code} className="space-y-1">
                    <div className="flex justify-between text-[10px] text-gray-300">
                      <span className="flex items-center gap-1">
                        <span className="px-1 py-0.2 bg-cyan-950 text-cyan-400 rounded font-bold text-[8px] uppercase">{c.code}</span>
                        {c.name}
                      </span>
                      <span className="font-bold text-cyan-400">{c.count.toLocaleString()}</span>
                    </div>
                    <div className="w-full h-1.5 bg-[#0f172a] rounded overflow-hidden">
                      <div
                        className="h-full bg-cyan-500/80 rounded transition-all duration-500 shadow-[0_0_8px_rgba(6,182,212,0.4)]"
                        style={{ width: `${c.percent}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Attack Vectors */}
            <div>
              <span className="text-[9px] uppercase tracking-wider text-rose-500/60 font-bold block mb-2">Top Attack Types</span>
              <div className="space-y-2">
                {topAttacks.map((a) => (
                  <div key={a.name} className="space-y-1">
                    <div className="flex justify-between text-[10px] text-gray-300">
                      <span>{a.name}</span>
                      <span className="font-bold text-rose-400">{a.percent}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-[#0f172a] rounded overflow-hidden">
                      <div
                        className="h-full bg-rose-500/80 rounded transition-all duration-500 shadow-[0_0_8px_rgba(244,63,94,0.4)]"
                        style={{ width: `${a.percent}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Targeted Industries */}
            <div>
              <span className="text-[9px] uppercase tracking-wider text-amber-500/60 font-bold block mb-2">Targeted Industries</span>
              <div className="space-y-2">
                {topIndustries.map((ind) => (
                  <div key={ind.name} className="space-y-1">
                    <div className="flex justify-between text-[10px] text-gray-300">
                      <span>{ind.name}</span>
                      <span className="font-bold text-amber-400">{ind.percent}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-[#0f172a] rounded overflow-hidden">
                      <div
                        className="h-full bg-amber-500/80 rounded transition-all duration-500 shadow-[0_0_8px_rgba(245,158,11,0.4)]"
                        style={{ width: `${ind.percent}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ThreatMap;
